'use client';

import {
  PopoverContentSlot,
  PopoverTooltipContentSlot,
  PopoverTooltipSlot,
  PopoverTriggerSlot,
} from '@magicschool/spellbook/components/Popover/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import * as PopoverPrimitive from 'radix-ui/popover';

export type PopoverProps = Omit<React.ComponentProps<typeof PopoverPrimitive.Root>, 'dir'> & {
  /** Preferred placement of the floating panel relative to the trigger. Radix auto-flips if insufficient space. */
  side?: React.ComponentProps<typeof PopoverPrimitive.Content>['side'];
  'data-testid'?: string;
};

export const PopoverBase: React.FC<PopoverProps> = ({
  children,
  'data-testid': dataTestId,
  side,
  modal,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}) => {
  const { slots } = useSlots(children, {
    content: { Component: PopoverContentSlot, props: { side, ...props } },
    trigger: { Component: PopoverTriggerSlot, props: { 'data-testid': dataTestId } },
    tooltip: { Component: PopoverTooltipSlot, props: { 'data-testid': dataTestId } },
  });

  return (
    <PopoverPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      {slots.tooltip ?? slots.trigger}
      {slots.content}
    </PopoverPrimitive.Root>
  );
};

/**
 * A click-triggered floating panel for displaying interactive content that doesn't fit inline. Built on Radix Popover with positioning, focus management, and dismiss behavior.
 *
 * ### When to use
 * - When you need a click-triggered floating panel with interactive content (forms, menus, pickers)
 * - As a building block for custom composite widgets that aren't covered by existing components
 * - When you need a tooltip on the trigger alongside the popover content (use `Popover.Tooltip`)
 *
 * ### When NOT to use
 * - Several Spellbook components already use Popover internally or provide popover-like behavior. Prefer these purpose-built components when they fit your use case:
 *   - `Autocomplete`: searchable dropdown with option selection
 *   - `MultiSelect`: multi-option selection with chips
 *   - `DateInput` / `FilterChipDate`: date picker popover
 *   - `DropdownMenu`: action menu triggered by a button
 *   - `Tooltip`: non-interactive hover label for a single element
 *   - `HoverCard`: hover-triggered preview content (non-interactive, not accessible to keyboard or screen reader users — visual enhancement only)
 * - Only reach for `Popover` directly when none of the above components cover your needs
 *
 * ### API
 * - `side`: Preferred placement relative to the trigger: `'top'`, `'right'`, `'bottom'` (default), `'left'`. Radix auto-flips if space is insufficient.
 * - `modal`: When `true`, traps focus inside the popover and prevents interaction with the rest of the page
 * - `defaultOpen`: Uncontrolled initial open state
 * - `onOpenChange`: Callback fired when the popover opens or closes. Use with a controlled `open` prop for programmatic control.
 *
 * ### Composition (slots)
 * - `<Popover.Trigger>`: Wraps the element that opens the popover on click. Renders with `asChild` by default so the child element becomes the trigger. Must be included explicitly.
 * - `<Popover.Content>`: The floating panel content. Portaled to the document body with overlay styling, enter and exit animations, and scroll containment; closed content stays mounted until its exit animation ends. Must be included explicitly — if omitted, clicking the trigger does nothing.
 * - `<Popover.Tooltip>`: Optional wrapper that adds a hover tooltip to the trigger. Nest `Popover.Trigger` and `Popover.TooltipContent` inside it. Replaces the trigger slot when present.
 * - `<Popover.TooltipContent>`: Text content for the tooltip. Renders as a `Text` caption. Use as a child of `Popover.Tooltip`.
 *
 * ### Accessibility
 * - Radix handles `aria-expanded`, `aria-controls`, and focus return to the trigger on close automatically
 * - Use `modal={true}` when the popover contains a workflow that should trap focus (e.g., a form)
 * - The trigger element should have a visible label or `aria-label` so screen readers can announce what the popover does
 *
 * @headless Radix Popover root renders no element; target `popover-trigger` and `popover-content`.
 */
export const Popover = Object.assign(PopoverBase, {
  Trigger: PopoverTriggerSlot,
  Content: PopoverContentSlot,
  Tooltip: PopoverTooltipSlot,
  TooltipContent: PopoverTooltipContentSlot,
});
